import apiEndpointManager from "../config";

const CACHE_KEY = "betakitNewsFeed";
const CACHE_DURATION_MS = 6 * 60 * 60 * 1000; // 6 hours

interface NewsItem {
    title: string;
    link: string;
    pubDate: string;
    contentSnippet: string;
}

interface CachedNewsFeed {
    timestamp: number;
    items: NewsItem[];
}

export const fetchAndParseRss = async (): Promise<NewsItem[]> => {
    // 1. Check for valid cached data
    const cachedDataString = localStorage.getItem(CACHE_KEY);
    if (cachedDataString) {
        try {
            const cachedData: CachedNewsFeed = JSON.parse(cachedDataString);
            const isCacheValid = Date.now() - cachedData.timestamp < CACHE_DURATION_MS;

            if (isCacheValid) {
                console.log("Returning news feed from cache.");
                return cachedData.items;
            }
        } catch (error) {
            console.error("Failed to parse cached news data, clearing cache:", error);
            localStorage.removeItem(CACHE_KEY);
        }
    }

    // 2. If no valid cache, fetch from the backend
    const endpoint = await apiEndpointManager.getEndpoint();
    const feedUrl = `${endpoint}/feed`;

    try {
        const response = await fetch(feedUrl);

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.text();
        const parser = new window.DOMParser();
        const xml = parser.parseFromString(data, "application/xml");

        const parseError = xml.querySelector("parsererror");
        if (parseError) {
            throw new Error("Failed to parse RSS feed from backend.");
        }

        const items: NewsItem[] = Array.from(xml.querySelectorAll("item")).map((itemNode) => {
            const title = itemNode.querySelector("title")?.textContent || "";
            const link = itemNode.querySelector("link")?.textContent || "";
            const pubDate = itemNode.querySelector("pubDate")?.textContent || "";
            const description =
                itemNode.querySelector("description")?.textContent ||
                itemNode.querySelector("content\\:encoded")?.textContent ||
                "";

            const contentSnippet = description.replace(/<[^>]*>?/gm, "").substring(0, 150) + "...";

            return { title, link, pubDate, contentSnippet };
        });

        // 3. Store the new data in the cache
        try {
            const dataToCache: CachedNewsFeed = {
                timestamp: Date.now(),
                items: items,
            };
            localStorage.setItem(CACHE_KEY, JSON.stringify(dataToCache));
        } catch (error) {
            console.error("Failed to cache news feed data:", error);
        }

        return items;
    } catch (error) {
        console.error("Error fetching or parsing RSS feed from backend:", error);
        throw new Error("No news for now. Please check again later.");
    }
};
